<script lang="ts">
  import Card from "$components/tournament-team-card/index.svelte";
  import { uiApi } from "$lib/ui-api";
  import Standings from "../tournaments/_components/standings.svelte";
  import ImageComp from "$components/image.svelte";
  import Trustpilot from "$components/trustpilot/testimonials/slider.svelte";
  import HalfBanner from "$components/half-banner/index.svelte";
  import TitleBlock from "$components/breadcrumb/block.svelte";
  import LastBought from "$components/last-bougth/index.svelte";
  import BreadCrumbs from "$components/breadcrumb/index.svelte";
  import ListNotFound from "$components/list-not-found/index.svelte";
  import TrustpilotMob from "$components/trustpilot/starter.svelte";
  import MetaComponent from "$components/meta/index.svelte";
  import ContentSlider from "$components/content-slider/index.svelte";
  import type { PageData } from "./$types";
  import type { Subcategory } from "types/backend/models/cms/sub-category.entity";
  import type { ITournamentEvents } from "./+page.server";
  import { MessageModal, openModal } from "@thehood/svlib";
  import { serializeSchema, ticketToSchema } from "$lib/utils/ldjson";

  export let data: PageData;

  let page = 1;
  let perPage = 11;
  let hideLoadMore = false;

  async function loadMore() {
    try {
      const resp = await uiApi.getRaw(`/tournaments/api/more-events?slug=${slug}&page=${++page}&perPage=${perPage}`);
      if (resp.ok) {
        const tournament = (await resp.json()) as Partial<Subcategory & { events: ITournamentEvents[] }>;
        if (tournament.events?.length) {
          events.push(...tournament.events);
          events = events;
        } else {
          hideLoadMore = true;
        }
      }
    } catch (err) {
      openModal(MessageModal, { title: "Error", message: "Error getting events " });
      throw new Error("Can't load more events, something went wrong.");
    }
  }

  function toSchema(event: ITournamentEvents) {
    const { product } = event;
    const row = {
      title: event.title,
      datetime: new Date(event.datetime),
      tbc: event.tbc,
      slug: `${event.subcategory.slug}${event.slug}`,
      subcategory: {
        title: event.subcategory.title,
        slug: event.subcategory.slug,
      },
      performers: event.performers,
      venue: event.venue,
      qty: product?.qty || 0,
      priceFrom: product
        ? {
            proceedPrice: product.proceedPrice,
            currency: product.currency,
          }
        : undefined,
    };

    return serializeSchema(ticketToSchema(row));
  }

  // $: ({ tournament, standings, locale, pageInfo } = data);
  // $: ({ events, title, slug, colour, logo } = tournament);
  const { tournament, standings, locale, pageInfo } = data;
  let { events, title, slug, colour, logo } = tournament;
  const paths = [
    { url: "/", name: "home" },
    { url: `/{slug}`, name: `${title} Tickets` },
  ];

  function makeBgColour(colour?: string) {
    if (colour) {
      // return `background-color:${colour.trim()}20`;
      return `${colour.trim()}20`;
    }
  }
</script>

<MetaComponent meta={pageInfo?.meta} />

<svelte:head>
  {#each events || [] as row}
    {@html toSchema(row)}
  {/each}
</svelte:head>

<div class="content-grid">
  <!-- <div class="search-wrapper full-width">
    <SearchComp />
  </div> -->

  <TitleBlock {paths} title={`${title} Tickets`} bg={makeBgColour(colour)} noMargin={true}>
    <div class="logo-img desk-only">
      <ImageComp image={logo} />
    </div>
    <BreadCrumbs {paths}>
      <div class="logo-img mob-only">
        <ImageComp image={logo} />
      </div>
      <div class="title-container">
        <h1 class="sf-700">{title} Tickets</h1>
        {#if data?.tournament?.lastSold}
          <div class="last-wrap">
            <LastBought lastSold={data.tournament.lastSold} className={"desk-only"} />
          </div>
        {/if}
      </div>
    </BreadCrumbs>
  </TitleBlock>

  <div class="tournament-wrapper desk-only">
    <div class="tournament-content grid generic-content">
      <ContentSlider content={tournament?.content} />
      <div>
        <HalfBanner />
      </div>
    </div>
  </div>
  <div class="page-content grid">
    <div class="events-list">
      <div class="head grid">
        <h3 class="sf-700">Upcoming Games</h3>
        <!-- <div>
          <Filter bind:events />
        </div> -->
      </div>
      {#if events.length}
        {#each events as event}
          <Card {event} {locale} />
        {/each}
        <div class="btn-wrapper">
          {#if !hideLoadMore}
            <button on:click={loadMore} class="sf-500">Load More</button>
          {/if}
        </div>
      {:else}
        <ListNotFound message={`At the moment, there are no tickets available for the ${title} tournament.`} />
      {/if}
    </div>
    <div class="standings">
      <div>
        <h3 class="sf-700">{title} Tables</h3>
        {#if standings.length}
          <Standings {standings} />
        {:else}
          No results currently available.
        {/if}
      </div>
    </div>
  </div>
  <div class="trustpilot">
    <div class="desk-only">
      <h5>Trusted by thousands of fans</h5>
      <Trustpilot theme="light" className={""} />
    </div>
    <TrustpilotMob theme="light" className={"mob-only"} height="110px" />
  </div>
  <!-- <div class="bottom-content grid"> -->
  <!-- <div class="col1">text1</div> -->
  <!-- <div class="col2">text2</div> -->
  <!-- </div> -->
</div>

<style lang="scss">
  @use "@styles/breakpoint-mixins" as *;
  .content-grid {
    background: #fff;
    @include desktop {
      background: var(--background-color);
      margin-bottom: 120px; // Standard margin bottom before footer
    }

    :global(.title-banner) {
      height: 140px;
      @include desktop {
        height: 158px;
      }
    }
  }

  h3 {
    margin: 0;
    font-size: 1.4em;
    @include desktop {
      margin: 0;
      font-size: 2.5em;
    }
  }

  .logo-img {
    width: 2.5rem;
    height: 2.5rem;
    margin: auto;
    @include desktop {
      margin: unset;
      width: 2.75em;
      margin-top: 0.25em;
      padding-bottom: 1em;
    }

    &.desk-only {
      margin-right: 20px;
      width: 3.375em;
      height: 3.375em;
    }
  }

  .title-container {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.3em;
    position: relative;
    flex: 1;
    @include tablet {
      justify-content: start;
    }

    .last-wrap {
      position: absolute;
      right: 0em;
      top: -0.4em;
    }

    h1 {
      font-size: 1.25em;
      line-height: 30px;
      margin: 0;
      @include tablet {
        font-size: 1.85em;
      }
    }
  }

  .tournament-wrapper {
    margin: 4.375em 0;
    display: none;
    @include desktop {
      display: grid;
      padding-top: 1em;
    }
  }

  .page-content {
    margin: 1.25em 0;
    padding: 1.25em 0;

    @include desktop {
      margin: 0;
      grid-template-columns: 49% 49%;
    }

    .events-list {
      gap: 0.625em 1em;
      display: grid;
      place-content: start stretch;

      .head {
        display: grid;
        grid-template-columns: auto 2.8em;
        align-items: center;
        padding-bottom: 0.5em;
        @include desktop {
          padding-bottom: 1em;
        }
      }

      .btn-wrapper {
        padding-top: 1.13em;
        button {
          @include desktop {
            margin: auto;
            width: 21em;
          }
        }
      }
    }

    .standings {
      display: none;
      @include desktop {
        display: grid;
        align-content: space-between;
        h3 {
          margin-bottom: 0.65em;
        }
      }
    }
  }

  .trustpilot {
    background: #f8f8f8;
    padding: 35px 1em;
    margin-bottom: 1.25em;
    border-radius: 5px;
    display: flex;
    place-items: center;
    :global(> div) {
      width: 100%;
    }

    @include desktop {
      background: #f4f4f4;
      margin-bottom: 0;
      border-radius: 0;
    }

    .desk-only {
      h5 {
        color: #000;
        font-family: Nunito;
        font-size: 20px;
        font-style: normal;
        font-weight: 700;
        line-height: 30px; /* 150% */
        text-align: center;
        margin-bottom: 40px;
      }
    }
  }
</style>
